import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  ClientProjectRequest,
  ClientProjectResponse,
  ProjectDetailProps,
  ProjectRequest,
  ProjectResponse,
  ProjectScopeDetailResponse,
  ProjectScopeRequest,
  ProjectScopeResponse,
} from 'types/ProjectTypes';
import { buildQueryString } from 'utils/url';

import { APIResponse, BUSMAN_TAGS, busmanApiSlice } from './busmanApiSlice';
import { getApiErrorMessage } from './errors';

/**
 *  Project endpoints
 */
export const projectEndpoints = busmanApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Fetches all Project
    getProjects: builder.query({
      query: ({ projectStatus, managerId, clientContactId, includeArchive = false }) =>
        `/projects/${buildQueryString({
          status: projectStatus,
          manager_id: managerId,
          client_contact_id: clientContactId,
          include_archived: includeArchive,
        })}`,
      providesTags: [BUSMAN_TAGS.Projects],
      transformResponse: (response: APIResponse<ProjectResponse[]>) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    // Fetches Project by given project id
    getProjectById: builder.query({
      query: (projectId) => `/projects/${projectId}/`,
      providesTags: (_result, _err, projectId) => [{ type: BUSMAN_TAGS.Projects, id: projectId }],
      transformResponse: (response: APIResponse<ProjectDetailProps>) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    // Creates a new Project
    createProject: builder.mutation({
      query: (newProject: ProjectRequest) => ({
        url: '/projects/',
        method: 'post',
        body: newProject,
      }),
      invalidatesTags: [BUSMAN_TAGS.Projects],
      transformResponse: (response) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    // Updates project details by given ID
    updateProjectById: builder.mutation({
      query: (updateProject: ProjectRequest) => ({
        url: `/projects/${updateProject?.project_id}/`,
        method: 'put',
        body: updateProject,
      }),
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
      transformResponse: (response) => response.data,
      invalidatesTags: (_result, _err, arg) => [
        BUSMAN_TAGS.Projects,
        { type: BUSMAN_TAGS.Projects, id: arg.project_id },
      ],
    }),

    // "Deletes" a project
    archiveProject: builder.mutation({
      query: (id) => ({
        url: `projects/${id}/`,
        method: 'delete',
      }),
      invalidatesTags: [BUSMAN_TAGS.Projects],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useUpdateProjectByIdMutation,
  useGetProjectByIdQuery,
  useLazyGetProjectByIdQuery,
  useCreateProjectMutation,
  useArchiveProjectMutation,
} = projectEndpoints;

/**
 *  Project Client endpoints
 */
export const projectClientEndpoints = busmanApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Fetches all project client
    getProjectClient: builder.query({
      query: ({ clientId }) =>
        `client-contacts/${buildQueryString({
          client_id: clientId,
        })}`,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
      transformResponse: (response: APIResponse<ClientProjectResponse[]>) => response.data,
    }),

    // Creates a new client contact
    addProjectClient: builder.mutation({
      query: (newClient) => ({
        url: '/client-contacts/',
        method: 'post',
        body: newClient,
      }),
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
      transformResponse: (response) => response.data,
    }),

    // Update Client Contact
    updateClientContactById: builder.mutation({
      query: (updateClientContact: ClientProjectRequest) => ({
        url: `/client-contacts/${updateClientContact.id}/`,
        method: 'put',
        body: updateClientContact,
      }),
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
      transformResponse: (response) => response.data,
    }),

    // Delete Client contact
    deleteProjectClientContact: builder.mutation({
      query: (id) => ({
        url: `/client-contacts/${id}/`,
        method: 'delete',
      }),
    }),
  }),
});

export const {
  useGetProjectClientQuery,
  useAddProjectClientMutation,
  useDeleteProjectClientContactMutation,
  useUpdateClientContactByIdMutation,
} = projectClientEndpoints;

/**
 *  Project Scope Endpoints
 */
export const projectScopeEndpoints = busmanApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Fetch Project Scope
    getProjectScope: builder.query({
      query: ({ projectId, scopeStatus, includeArchive = false }) =>
        `/project-scopes/${buildQueryString({
          project_id: projectId,
          status: scopeStatus,
          include_archived: includeArchive,
        })}`,
      providesTags: [BUSMAN_TAGS.ProjectScopes],
      transformResponse: (response: APIResponse<ProjectScopeResponse[]>) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    // Fetch Project Detail
    getProjectScopeById: builder.query({
      query: (projectScopeId) => `/project-scopes/${projectScopeId}`,
      providesTags: (_result, _err, projectScopeId) => [{ type: BUSMAN_TAGS.ProjectScopes, id: projectScopeId }],
      transformResponse: (response: APIResponse<ProjectScopeDetailResponse>) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    // Creates a new Project Scope
    createProjectScope: builder.mutation({
      query: (newScope: ProjectScopeRequest) => ({
        url: '/project-scopes/',
        method: 'post',
        body: newScope,
      }),
      invalidatesTags: [BUSMAN_TAGS.Projects, BUSMAN_TAGS.ProjectScopes],
      transformResponse: (response) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    // Updates a Project Scope by given ID
    updateProjectScopeById: builder.mutation({
      query: (updateScope: ProjectScopeRequest) => ({
        url: `/project-scopes/${updateScope?.id}/`,
        method: 'put',
        body: updateScope,
      }),
      invalidatesTags: (_result, _err, arg) => [
        BUSMAN_TAGS.Projects,
        BUSMAN_TAGS.ProjectScopes,
        { type: BUSMAN_TAGS.ProjectScopes, id: arg.id },
      ],
      transformResponse: (response) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),

    approveScopeByScopeId: builder.mutation({
      query: (approveScope: ProjectScopeRequest) => ({
        url: `/project-scopes/${approveScope.id}/approve/`,
        method: 'put',
        body: approveScope,
      }),
      invalidatesTags: (_result, _err, arg) => [
        BUSMAN_TAGS.Projects,
        BUSMAN_TAGS.ProjectScopes,
        { type: BUSMAN_TAGS.ProjectScopes, id: arg.id },
      ],
      transformResponse: (response) => response.data,
      transformErrorResponse: (response: FetchBaseQueryError) => getApiErrorMessage(response),
    }),
  }),
});

export const {
  useGetProjectScopeQuery,
  useGetProjectScopeByIdQuery,
  useCreateProjectScopeMutation,
  useUpdateProjectScopeByIdMutation,
  useLazyGetProjectScopeByIdQuery,
  useApproveScopeByScopeIdMutation,
} = projectScopeEndpoints;
